// Enhanced Langfuse integration for frontend-to-backend tracing via proxy
import { langfuseProxy } from '@/utils/langfuseProxy';

// Determine if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Debug logging for initialization
console.log('Langfuse Enhanced client using server-side proxy:', {
  environment: isBrowser ? 'browser' : 'server',
  baseUrl: process.env.NEXT_PUBLIC_LANGFUSE_BASE_URL || 'https://analytics.aitana.chat',
  publicKeyConfigured: !!process.env.NEXT_PUBLIC_LANGFUSE_PUBLIC_KEY,
  usingProxy: true
});

export { langfuseProxy as langfuseClient };

/**
 * Flush Langfuse events immediately via proxy
 * Important for serverless environments where process might terminate quickly
 */
export async function flushLangfuseEvents(): Promise<void> {
  try {
    console.log('Flushing Langfuse events via proxy...');
    await langfuseProxy.flush();
    console.log('✅ Langfuse events flushed successfully via proxy');
  } catch (error) {
    console.warn('❌ Error flushing Langfuse events via proxy:', error);
    throw error; // Re-throw so caller knows it failed
  }
}

/**
 * Create a trace that starts immediately when user clicks send via proxy
 * This captures the full end-to-end latency from UI interaction to backend response
 */
export async function createUserInteractionTrace(params: {
  traceId: string;
  sessionId: string;
  userId?: string;
  userEmail?: string;
  assistantId: string;
  assistantName?: string;
  message: string;
  selectedItemsCount?: number;
}) {
  try {
    const traceName = params.assistantName || 'Assistant';
    
    await langfuseProxy.createTrace({
      traceId: params.traceId,
      name: traceName,  // Use assistant name
      sessionId: params.sessionId,
      userId: params.userEmail || params.userId,
      metadata: {
        assistantId: params.assistantId,
        assistantName: params.assistantName,
        messageLength: params.message.length,
        selectedItemsCount: params.selectedItemsCount || 0,
        source: 'frontend-click',
        timestamp: new Date().toISOString(),
        environment: isBrowser ? 'browser' : 'server',
      },
      input: {
        message: params.message,
      }
    });


    // Create initial span for UI processing via proxy
    await langfuseProxy.createSpan({
      traceId: params.traceId,
      spanName: 'frontend-ui-processing',
      metadata: {
        stage: 'message-preparation',
        source: 'frontend',
        timestamp: new Date().toISOString()
      }
    });


    // Return a simplified trace object that contains the ID for future operations
    return { 
      trace: { id: params.traceId }, 
      uiSpan: { name: 'frontend-ui-processing', traceId: params.traceId } 
    };
  } catch (error) {
    console.warn('Failed to create trace/span via proxy:', error);
    return { 
      trace: { id: params.traceId }, 
      uiSpan: { name: 'frontend-ui-processing', traceId: params.traceId } 
    };
  }
}

/**
 * Mark when the message is ready to be sent to backend via proxy
 */
export async function markMessageReadyForBackend(uiSpan: any, metadata?: Record<string, any>) {
  try {
    await langfuseProxy.endSpan({
      traceId: uiSpan.traceId,
      spanName: uiSpan.name,
      output: { status: 'ready' },
      metadata: {
        ...metadata,
        stage: 'ready-for-backend',
        source: 'frontend'
      }
    });
  } catch (error) {
    console.warn('Failed to end UI span via proxy:', error);
  }
}

/**
 * Create a span for the API call to backend via proxy
 */
export async function createBackendCallSpan(trace: any, metadata?: Record<string, any>) {
  try {
    await langfuseProxy.createSpan({
      traceId: trace.id,
      spanName: 'frontend-backend-api-call',
      metadata: {
        endpoint: '/api/proxy',
        source: 'frontend',
        ...metadata
      }
    });
    
    // Return a simplified span object for future operations
    return { name: 'frontend-backend-api-call', traceId: trace.id };
  } catch (error) {
    console.warn('Failed to create backend call span via proxy:', error);
    return { name: 'frontend-backend-api-call', traceId: trace.id };
  }
}

/**
 * Mark when we receive the first response from backend via proxy
 */
export async function markFirstBackendResponse(
  backendSpan: any, 
  trace: any,
  responseTime: number
) {
  try {
    // Note: The current proxy doesn't support span updates, so we'll create a generation instead
    await langfuseProxy.createGeneration({
      traceId: trace.id,
      name: 'ui-streaming-response',
      model: 'streaming',
      metadata: {
        frontendToBackendLatency: responseTime,
        firstResponseTime: responseTime,
        firstResponseReceived: new Date().toISOString(),
        streaming: true
      }
    });

    // Return a simplified generation object for future operations
    return { name: 'streaming-response', traceId: trace.id };
  } catch (error) {
    console.warn('Failed to create streaming generation via proxy:', error);
    return { name: 'streaming-response', traceId: trace.id };
  }
}

/**
 * Update streaming progress (simplified for proxy usage)
 * Note: Proxy doesn't support generation updates, so this is mainly for logging
 */
export function updateStreamingProgress(
  generation: any,
  chunkCount: number,
  contentLength: number
) {
  // Log progress instead of updating since proxy doesn't support generation updates
  console.log('Streaming progress:', {
    generationName: generation.name,
    traceId: generation.traceId,
    chunkCount,
    contentLength,
    lastUpdate: new Date().toISOString()
  });
}

/**
 * Complete the streaming generation (simplified for proxy usage)
 * Note: Proxy doesn't support generation end operations, so this is mainly for logging
 */
export function completeStreamingGeneration(
  generation: any,
  finalContent: string,
  totalDuration: number,
  metadata?: Record<string, any>
) {
  // Log completion instead of ending since proxy doesn't support generation end
  console.log('Streaming generation completed:', {
    generationName: generation.name,
    traceId: generation.traceId,
    totalDuration,
    finalContentLength: finalContent.length,
    completedAt: new Date().toISOString(),
    ...metadata
  });
}

/**
 * Complete the entire trace via proxy
 */
export async function completeUserInteractionTrace(
  trace: any,
  backendSpan: any,
  success: boolean,
  totalDuration: number,
  metadata?: Record<string, any>
) {
  try {
    // End backend span via proxy
    await langfuseProxy.endSpan({
      traceId: trace.id,
      spanName: backendSpan.name,
      output: { success },
      metadata: {
        totalDuration,
        ...metadata
      }
    });

    console.log('User interaction trace completed:', {
      traceId: trace.id,
      success,
      totalDuration,
      completedAt: new Date().toISOString(),
      ...metadata
    });

    // Flush events via proxy
    await langfuseProxy.flush();
  } catch (error) {
    console.warn('Failed to complete user interaction trace via proxy:', error);
  }
}

/**
 * Create an error span for failed operations via proxy
 */
export async function createErrorSpan(trace: any, error: Error, stage: string) {
  try {
    // Create error span via proxy
    await langfuseProxy.createSpan({
      traceId: trace.id,
      spanName: 'frontend-error',
      metadata: {
        stage,
        source: 'frontend',
        errorType: error.name,
        errorStack: error.stack,
        level: 'ERROR',
        statusMessage: error.message
      }
    });
    
    // Immediately end the error span
    await langfuseProxy.endSpan({
      traceId: trace.id,
      spanName: 'frontend-error',
      output: { error: error.message }
    });
  } catch (proxyError) {
    console.warn('Failed to create error span via proxy:', proxyError);
  }
}

/**
 * Helper to measure latency between stages
 */
export class LatencyTracker {
  private startTime: number;
  private stages: Map<string, number>;

  constructor() {
    this.startTime = Date.now();
    this.stages = new Map();
    this.stages.set('start', this.startTime);
  }

  mark(stage: string): number {
    const now = Date.now();
    this.stages.set(stage, now);
    return now - this.startTime;
  }

  getLatency(fromStage: string, toStage: string): number {
    const from = this.stages.get(fromStage);
    const to = this.stages.get(toStage);
    
    if (!from || !to) {
      return 0;
    }
    
    return to - from;
  }

  getTotalLatency(): number {
    return Date.now() - this.startTime;
  }

  getAllLatencies(): Record<string, number> {
    const latencies: Record<string, number> = {};
    let previousStage = 'start';
    
    for (const [stage, time] of this.stages) {
      if (stage !== 'start') {
        latencies[`${previousStage}_to_${stage}`] = this.getLatency(previousStage, stage);
        previousStage = stage;
      }
    }
    
    latencies.total = this.getTotalLatency();
    return latencies;
  }
}